import { useState, useEffect } from 'react';
import { Trash2, CalendarDays, AlertCircle } from 'lucide-react';
import { pressureService } from '../services/api.js';
import { getBPStatus } from '../utils/health.js';

const DAILY_SLOTS = [
  { category: 'Morning', time: '08:30' },
  { category: 'Evening', time: '18:30' },
  { category: 'Night',   time: '22:30' },
];

const formatDate = (dateStr) => {
  const d = new Date(dateStr + 'T12:00:00');
  const day  = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const week = d.toLocaleDateString('en-US', { weekday: 'long' });
  return { day, week };
};

// ── SlotRow ─────────────────────────────────────────────────────────────────
const SlotRow = ({ slot, reading, onDelete }) => {
  const isEmpty = !reading;
  const status  = isEmpty ? null : getBPStatus(reading.systolic, reading.diastolic);

  if (isEmpty) {
    return (
      <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-4 px-4 sm:px-6 py-3 sm:py-4 border-b border-[#F5F5F5] last:border-0 opacity-50">
        <div className="w-1.5 h-1.5 rounded-full bg-[#EEEEEE] hidden sm:block" />
        <span className="text-[10px] sm:text-[11px] font-bold text-[#999999] uppercase tracking-wider w-auto sm:w-20">
          {slot.category}
        </span>
        <div className="flex items-center gap-2 flex-1">
          <span className="text-[9px] sm:text-[10px] font-bold text-[#EEEEEE] uppercase tracking-[0.2em]">
            No Record
          </span>
        </div>
        <span className="text-[9px] sm:text-[10px] font-bold text-[#EEEEEE] tracking-tight">{slot.time}</span>
      </div>
    );
  }

  return (
    <div className={`group flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-4 px-4 sm:px-6 py-3 sm:py-4 border-b border-[#F5F5F5] last:border-0 transition-all`}>
      <div className={`w-1.5 h-1.5 rounded-full ${status.dot} hidden sm:block`} />
      
      <span className="text-[10px] sm:text-[11px] font-bold text-[#999999] uppercase tracking-wider w-auto sm:w-16 md:w-20">
        {slot.category}
      </span>

      <div className="flex items-baseline gap-1 sm:gap-1.5 flex-1">
        <span className={`text-xl sm:text-2xl font-extrabold tracking-tighter text-[#111111]`}>
          {reading.systolic}
          <span className="text-[#DDDDDD] mx-0.5 font-light">/</span>
          {reading.diastolic}
        </span>
        <span className="text-[9px] sm:text-[10px] font-bold text-[#BBBBBB] uppercase">mmHg</span>
      </div>

      <div className="flex items-center gap-2 sm:gap-6">
        <span className={`text-[8px] sm:text-[9px] font-extrabold uppercase tracking-widest px-2 py-1 sm:px-2.5 sm:py-1 rounded-full ${status.badge} w-24 sm:w-32 text-center shadow-sm`}>
          {status.label}
        </span>

        <div className="opacity-100 sm:opacity-0 group-hover:opacity-100 transition-all">
          <button
            onClick={() => onDelete(reading.id)}
            className="p-2 rounded-xl bg-white hover:bg-black text-[#CCCCCC] hover:text-white border border-[#F1F1F1] hover:border-black transition-all shadow-sm"
            title="Delete Record"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ── DayBlock ─────────────────────────────────────────────────────────────────
const DayBlock = ({ dateStr, readings, onDelete }) => {
  const { day, week } = formatDate(dateStr);
  const readingsByCategory = {};
  for (const r of readings) readingsByCategory[r.category] = r;

  return (
    <div className="modern-card !p-0 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-500">
      <div className="flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-4 sm:py-5 border-b border-[#F1F1F1] bg-[#FAFAFA]/50">
        <div className="p-2 sm:p-2.5 bg-white border border-[#F1F1F1] rounded-2xl shadow-sm text-[#111111]">
          <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5" />
        </div>
        <div>
          <p className="text-sm sm:text-base font-extrabold text-[#111111] tracking-tight">{day}</p>
          <p className="text-[9px] sm:text-[10px] font-bold text-[#777777] uppercase tracking-widest">{week}</p>
        </div>
        <div className="ml-auto text-[9px] sm:text-[10px] font-black text-[#BBBBBB] uppercase tracking-[0.2em] bg-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border border-[#F1F1F1]">
          {readings.length}/3
        </div>
      </div>

      <div className="bg-white">
        {DAILY_SLOTS.map(slot => (
          <SlotRow
            key={slot.category}
            slot={slot}
            reading={readingsByCategory[slot.category] || null}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
};

// ── HistoryList (main export) ─────────────────────────────────────────────────
export const HistoryList = ({ refreshTrigger }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    fetchHistory();
  }, [refreshTrigger]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await pressureService.getLatestReadings(15);
      setHistory(response.data);
    } catch (err) {
      console.error('Failed to load history:', err);
      setError('Connection failed. Please retry.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confirm deletion of this record?')) return;
    try {
      // Note: deleteReading needs implementation in api.js if not present
      // For now, filtering locally to simulate success
      setHistory(prev =>
        prev
          .map(day => ({ ...day, readings: day.readings.filter(r => r.id !== id) }))
          .filter(day => day.readings.length > 0)
      );
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="modern-card !p-0 overflow-hidden animate-pulse">
            <div className="h-16 bg-[#FAFAFA] border-b border-[#F1F1F1]" />
            {[1, 2, 3].map(j => (
              <div key={j} className="h-14 border-b border-[#F1F1F1]" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="modern-card text-center py-16 bg-red-50/30 border-red-100">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
        <p className="text-red-700 font-extrabold uppercase tracking-widest text-xs">{error}</p>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="modern-card text-center py-24 border-dashed bg-[#FAFAFA]/30">
        <p className="text-[#AAAAAA] font-extrabold uppercase tracking-widest text-xs">No entries recorded</p>
        <p className="text-[#999999] text-[11px] mt-2 font-bold uppercase">Begin logging to populate your clinical history</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {history.map(({ date, readings }) => (
        <DayBlock
          key={date}
          dateStr={date}
          readings={readings}
          onDelete={handleDelete}
        />
      ))}
    </div>
  );
};

